import { AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import { CheckoutInvoice, Invoice } from '@/types/Invoice';
import {
    FetchInvoicePayload,
    ReopenInvoicePayload,
    SubmitShippingMethodPayload,
} from '@/redux/modules/checkout/checkout.types';
import { getAuthToken, getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getCatalogBillingCheckout } from '../catalog/catalogs/catalog.selectors';
import { getDeployment } from '@/redux/modules/config';
import { getInvoice, postReopenPaidInvoice, postShippingMethod } from './checkout.api';
import { shouldFetchInvoice } from '@/redux/modules/checkout/checkout.selectors';

export const fetchInvoice = createAsyncThunk<CheckoutInvoice, number, AsyncThunkConfigWithRejectValue>(
    'la/domain/checkout/fetchInvoice',
    async (catalogId, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const bidderId = getBidderId(state);
            const billingCheckout = getCatalogBillingCheckout(state, catalogId);

            if (!Boolean(bidderId) || bidderId < 0) {
                return null;
            }

            const { data } = await getInvoice({
                authToken,
                bidderId,
                billingCheckout,
                catalogId,
                deployment,
            });
            return data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const fetchInvoiceIfNeeded = createAsyncThunk<void, FetchInvoicePayload>(
    'la/domain/checkout/fetchInvoiceIfNeeded',
    async ({ catalogId, force = false }, { dispatch, getState }) => {
        const state = getState();
        if (force || shouldFetchInvoice(state, catalogId)) {
            await dispatch(fetchInvoice(catalogId));
        }
    }
);

export const reopenPaidInvoice = createAsyncThunk<Partial<Invoice>, ReopenInvoicePayload>(
    'la/domain/checkout/reopenPaidInvoice',
    async ({ catalogId, invoiceId, shippingAddressId }, { dispatch, getState, rejectWithValue }) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);

            if (!Boolean(bidderId) || bidderId < 0) {
                return null;
            }

            const { payload } = await postReopenPaidInvoice({
                authToken,
                bidderId,
                deployment,
                invoiceId,
                shippingAddressId,
            });

            await dispatch(fetchInvoice(catalogId));

            return payload;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const submitShippingMethod = createAsyncThunk<void, SubmitShippingMethodPayload>(
    'la/domain/checkout/submitShippingMethod',
    async ({ invoiceId, shippingMethod }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        await postShippingMethod({
            authToken,
            bidderId,
            deployment,
            invoiceId,
            shippingMethod,
        });
    }
);
